import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Box, Button, IconButton, Modal, Popper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { TourPlacement } from "./types";

type Props = {
  target: HTMLElement;
  title: string;
  body: string;
  stepLabel: string;
  nextLabel: string;
  closeLabel: string;
  placement: TourPlacement;
  onNext: () => void;
  onDismiss: () => void;
};

const PAD = 6;

const useTargetRect = (target: HTMLElement) => {
  const [rect, setRect] = useState(() => target.getBoundingClientRect());
  useLayoutEffect(() => {
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setRect(target.getBoundingClientRect()));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(target);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      observer?.disconnect();
    };
  }, [target]);
  return rect;
};

/** Dims the page with a cutout around `target` and anchors a dark card to it. */
const TourStepPopover = ({
  target,
  title,
  body,
  stepLabel,
  nextLabel,
  closeLabel,
  placement,
  onNext,
  onDismiss,
}: Props) => {
  const rect = useTargetRect(target);
  // State, not a ref: Popper needs the arrow element once it exists.
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    target.scrollIntoView?.({ block: "center", inline: "nearest" });
  }, [target]);

  // Popper mounts its content a tick late, so focus the button when it
  // attaches (after any dialog underneath has taken focus), not on mount.
  const focusOnAttach = useCallback((el: HTMLButtonElement | null) => el?.focus(), []);

  // A Modal, not a bare portal: it becomes the top modal, so an open
  // Settings dialog underneath (team tour step 3) stands its focus trap
  // down, focus stays in the card, and Esc closes the tour, not the dialog.
  return (
    <Modal
      open
      hideBackdrop
      // The Next button autofocuses; the trap keeps focus inside from there.
      disableAutoFocus
      onClose={(_e, reason) => {
        if (reason === "escapeKeyDown") onDismiss();
      }}
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
    >
      <Box sx={{ position: "fixed", inset: 0, outline: "none" }}>
      <Box
        data-testid="tour-scrim"
        onClick={(e) => e.stopPropagation()}
        sx={{ position: "fixed", inset: 0 }}
      >
        <svg width="100%" height="100%" aria-hidden style={{ display: "block" }}>
          <defs>
            <mask id="tour-cutout">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={rect.left - PAD}
                y={rect.top - PAD}
                width={rect.width + PAD * 2}
                height={rect.height + PAD * 2}
                rx={8}
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="var(--mui-palette-grey-900)"
            fillOpacity={0.55}
            mask="url(#tour-cutout)"
          />
        </svg>
      </Box>
      <Popper
        open
        disablePortal
        anchorEl={target}
        placement={placement}
        role="dialog"
        aria-labelledby="tour-step-title"
        modifiers={[
          { name: "offset", options: { offset: [0, 14] } },
          { name: "arrow", enabled: !!arrowEl, options: { element: arrowEl } },
          { name: "preventOverflow", options: { padding: 12 } },
        ]}
        sx={(t) => ({
          zIndex: 1,
          maxWidth: 320,
          "&[data-popper-placement^='right'] .tour-arrow": { left: -6 },
          "&[data-popper-placement^='left'] .tour-arrow": { right: -6 },
          "&[data-popper-placement^='bottom'] .tour-arrow": { top: -6 },
          "&[data-popper-placement^='top'] .tour-arrow": { bottom: -6 },
        })}
      >
        <Box
          sx={(t) => ({
            position: "relative",
            bgcolor: "grey.900",
            color: "common.white",
            borderRadius: 2,
            p: 2,
            boxShadow: 8,
            ...t.applyStyles("dark", { bgcolor: "grey.100", color: "grey.900" }),
          })}
        >
          <Box
            ref={setArrowEl}
            component="span"
            className="tour-arrow"
            sx={(t) => ({
              position: "absolute",
              width: 12,
              height: 12,
              bgcolor: "grey.900",
              transform: "rotate(45deg)",
              ...t.applyStyles("dark", { bgcolor: "grey.100" }),
            })}
          />
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Typography id="tour-step-title" variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              {title}
            </Typography>
            <IconButton
              aria-label={closeLabel}
              onClick={onDismiss}
              size="small"
              sx={{ color: "inherit", mt: -0.5, mr: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }} aria-live="polite">
            {body}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
            <Button
              ref={focusOnAttach}
              onClick={onNext}
              size="small"
              variant="outlined"
              sx={{ color: "inherit", borderColor: "currentColor", textTransform: "none", fontWeight: 600 }}
            >
              {nextLabel}
            </Button>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {stepLabel}
            </Typography>
          </Box>
        </Box>
      </Popper>
      </Box>
    </Modal>
  );
};

export default TourStepPopover;
